import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from '@/components/Header';
import PaperDetailPanel from '@/components/PaperDetailPanel';
import { loadPapers, loadConfig, loadKeywordLinks } from '@/lib/data';
import { buildGraphData, createRelationshipGraph, RELATIONSHIP_TYPES } from '@/viz/graph';
import { humanize } from '@/lib/humanize';

export default function RelationshipsPage() {
  const [papers, setPapers] = useState(null);
  const [config, setConfig] = useState(null);
  const [keywordLinks, setKeywordLinks] = useState([]);
  const containerRef = useRef(null);
  const vizRef = useRef(null);

  const [showLabels, setShowLabels] = useState(true);
  const [showClusters, setShowClusters] = useState(true);
  const [showSimilar, setShowSimilar] = useState(true);
  const [similarityPercentile, setSimilarityPercentile] = useState(0.9);
  const [nodeSpacing, setNodeSpacing] = useState(1.5);

  const [hovered, setHovered] = useState(null);
  const [selectedPaperId, setSelectedPaperId] = useState(null);

  useEffect(() => {
    Promise.all([loadPapers(), loadConfig(), loadKeywordLinks()]).then(([p, c, kl]) => {
      setPapers(p);
      setConfig(c);
      setKeywordLinks(kl);
    });
  }, []);

  const artifactColors = useMemo(() => {
    const map = {};
    (config?.typesOfArtifact || []).forEach((t) => {
      map[t.id] = t.color;
    });
    return map;
  }, [config]);

  const handleZoomIn = useCallback(() => vizRef.current?.zoomIn(), []);
  const handleZoomOut = useCallback(() => vizRef.current?.zoomOut(), []);
  const handleZoomReset = useCallback(() => vizRef.current?.zoomToFit(), []);
  const handleRestart = useCallback(() => vizRef.current?.resetSimulation(), []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const active = document.activeElement;
      if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); handleZoomIn(); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); handleZoomOut(); }
      else if (e.key === '0') { e.preventDefault(); handleZoomReset(); }
      else if (e.key === 'r' || e.key === 'R') { e.preventDefault(); handleRestart(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleZoomReset, handleRestart]);

  useEffect(() => {
    if (!papers || !containerRef.current || !config) return undefined;
    const data = buildGraphData(papers, artifactColors, {
      showSimilar,
      similarityPercentile,
      precomputedKeywordLinks: keywordLinks,
    });
    vizRef.current = createRelationshipGraph(containerRef.current, data, {
      typesOfArtifact: config.typesOfArtifact || [],
      showLabels,
      showClusters,
      nodeSpacing,
      onNodeClick: (id) => setSelectedPaperId(id),
      onNodeHover: setHovered,
      onNodeLeave: () => setHovered(null),
    });
    return () => vizRef.current?.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [papers, config, artifactColors, showSimilar, similarityPercentile, nodeSpacing, keywordLinks]);

  useEffect(() => {
    vizRef.current?.setShowLabels(showLabels);
  }, [showLabels]);

  useEffect(() => {
    vizRef.current?.setShowClusters(showClusters);
  }, [showClusters]);

  if (!papers || !config) {
    return (
      <div className="main-layout">
        <div className="loading">
          <div className="loading__spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const labels = config.visualization?.labels || {};

  return (
    <div className="main-layout">
      <Header config={config} />
      <main className="main-content">
        <h1>{labels.graphTitle || 'Paper Relationship Graph'}</h1>
        {labels.graphDescription && <p className="graph-page__description">{labels.graphDescription}</p>}

        <div className="graph-info-cards">
          <div className="graph-info-card">
            <span className="graph-info-card__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 9 2 12 5 15" />
                <polyline points="9 5 12 2 15 5" />
                <polyline points="15 19 12 22 9 19" />
                <polyline points="19 9 22 12 19 15" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <line x1="12" y1="2" x2="12" y2="22" />
              </svg>
            </span>
            <div>
              <h3>Drag &amp; Zoom</h3>
              <p>Drag nodes to rearrange. Scroll to zoom. Pan with mouse.</p>
            </div>
          </div>
          <div className="graph-info-card">
            <span className="graph-info-card__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </span>
            <div>
              <h3>Clusters</h3>
              <p>Papers are grouped by type of artifact. Hulls show cluster boundaries.</p>
            </div>
          </div>
          <div className="graph-info-card">
            <span className="graph-info-card__icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
            </span>
            <div>
              <h3>Connections</h3>
              <p>Solid lines = similarity. Dashed = shared keywords.</p>
            </div>
          </div>
        </div>

        <div className="graph-frame">
        <div className="graph-controls">
          <label className="graph-controls__toggle">
            <input type="checkbox" checked={showClusters} onChange={(e) => setShowClusters(e.target.checked)} />
            <span>Show Clusters</span>
          </label>
          <label className="graph-controls__toggle">
            <input type="checkbox" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />
            <span>Show Labels</span>
          </label>
          <label className="graph-controls__toggle">
            <input type="checkbox" checked={showSimilar} onChange={(e) => setShowSimilar(e.target.checked)} />
            <span>Similar Research Artifacts</span>
          </label>
          {showSimilar && (
            <div className="graph-controls__slider">
              <label>Similarity: top {Math.round((1 - similarityPercentile) * 100)}%</label>
              <input
                type="range"
                min="0.7"
                max="0.98"
                step="0.01"
                value={similarityPercentile}
                onChange={(e) => setSimilarityPercentile(parseFloat(e.target.value))}
              />
            </div>
          )}
          <div className="graph-controls__slider">
            <label>Spacing: {nodeSpacing.toFixed(1)}x</label>
            <input
              type="range"
              min="0.5"
              max="3.0"
              step="0.1"
              value={nodeSpacing}
              onChange={(e) => setNodeSpacing(parseFloat(e.target.value))}
            />
          </div>
          <div className="graph-controls__spacer" />
          <span className="graph-controls__hint">Scroll to zoom · drag nodes · +/- zoom · 0 reset · R restart</span>
          <button className="graph-controls__btn" onClick={handleZoomOut} title="Zoom Out (−)" aria-label="Zoom out">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </button>
          <button className="graph-controls__btn" onClick={handleZoomIn} title="Zoom In (+)" aria-label="Zoom in">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </button>
          <button className="graph-controls__btn" onClick={handleZoomReset} title="Reset View (0)" aria-label="Reset view">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </button>
          <button className="graph-controls__btn" onClick={handleRestart} title="Restart Simulation (R)" aria-label="Restart simulation">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
          </button>
        </div>

        <div className="graph-canvas-wrap">
          <div className="graph-page__canvas" ref={containerRef} />

          {hovered && (
            <div className="graph-hover-card">
              {hovered.userAdded && <span className="user-added-badge">+ Added by you</span>}
              <h4>{hovered.title || 'Anonymized paper'}</h4>
              <div className="graph-hover-card__meta">
                {hovered.typeOfArtifact.map((t) => (
                  <span key={t} style={{ color: artifactColors[t] }}>{humanize(t)}</span>
                ))}
                {hovered.year && <span>· {hovered.year}</span>}
              </div>
              {hovered.keywords.length > 0 && (
                <div className="graph-hover-card__tags">
                  {hovered.keywords.slice(0, 6).map((k) => (
                    <span key={k} className="chip chip--sm">{k}</span>
                  ))}
                </div>
              )}
              <p className="graph-hover-card__hint">
                {hovered.degree} connection{hovered.degree !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>

        <div className="graph-legend">
          <span className="graph-legend__title">Relationships</span>
          <div className="graph-legend__items">
            {Object.entries(RELATIONSHIP_TYPES).map(([key, style]) => (
              <span key={key} className="graph-legend__item">
                <svg width="24" height="10" viewBox="0 0 24 10">
                  <line x1="0" y1="5" x2="24" y2="5" stroke={style.color} strokeWidth={style.strokeWidth} strokeDasharray={style.dashArray || 'none'} />
                </svg>
                {style.label}
              </span>
            ))}
          </div>
        </div>
        </div>
      </main>

      {selectedPaperId && (
        <PaperDetailPanel
          paperId={selectedPaperId}
          papers={papers}
          config={config}
          onClose={() => setSelectedPaperId(null)}
          onSelectPaper={setSelectedPaperId}
          onRemoved={() => loadPapers().then(setPapers)}
        />
      )}
    </div>
  );
}
